import { type InputRow, type MatchedRow } from "./types";
import { reasonText, statusText } from "./messages";

/**
 * 法人の確認画面の1行を、表・SPのカード・CSV出力で同じ表示にするための値。
 * 特定した法人があればその値を、なければ入力値を出す。
 */
export interface RowView {
  rowNumber: number;
  companyName: string;
  inputCompanyName: string;
  city: string;
  corporateNumber: string;
  corporateIdentificationNumber: string;
  status: string;
  target: string;
  action: string;
  reasons: string[];
  confidence: string;
}

const EMPTY = "—";
// 入力値は未入力のとき空文字で届くため、空文字も未入力として表示する
const orEmpty = (value: string): string => (value === "" ? EMPTY : value);
const orMissing = (value: string | undefined): string =>
  value === undefined || value === "" ? "未入力" : value;

/** 照合結果と入力行から、画面とCSVに出す値を作る */
export const toRowView = (
  row: MatchedRow,
  input: InputRow | undefined
): RowView => {
  const corporation = row.corporation;
  return {
    rowNumber: row.rowNumber,
    companyName:
      corporation?.companyName ??
      (input?.companyName ? input.companyName : EMPTY),
    inputCompanyName: input?.companyName ?? "",
    city: corporation
      ? `${corporation.prefectureName}${corporation.cityName}`
      : orEmpty(`${input?.prefectureName ?? ""}${input?.cityName ?? ""}`),
    corporateNumber:
      corporation?.corporateNumber ??
      (row.status === "NEEDS_REVIEW" && row.candidateCount > 0
        ? `候補 ${row.candidateCount.toLocaleString()}件`
        : orMissing(input?.corporateNumber)),
    corporateIdentificationNumber:
      corporation?.corporateIdentificationNumber ??
      orMissing(input?.corporateIdentificationNumber),
    status: statusText[row.status],
    target:
      row.targetState === "TARGET"
        ? "対象"
        : row.targetState === "HELD"
        ? "保留"
        : "除外",
    action: actionLabel(row),
    reasons: [...row.reasonCodes, ...row.warningCodes].map(reasonText),
    confidence: row.confidence === "HIGH" ? "確度 高" : "",
  };
};

/** 行の状態に合わせた操作の文言。押すと同じ確認・修正のダイアログを開く */
const actionLabel = (row: MatchedRow): string => {
  if (row.status === "NEEDS_REVIEW") return "候補を選択";
  if (row.status === "UNMATCHED") return "番号を入力";
  if (row.status === "ERROR") return "内容を確認";
  return "詳細";
};

/** 画面の絞り込みの選択肢。「要確認」は未特定を含む（集計カードと同じ数え方） */
export const statusFilters = {
  ALL: "すべてのステータス",
  REVIEW: "要確認",
  ERROR: "エラー",
  DUPLICATE: "重複",
  TARGET: "取得対象",
} as const;
export type StatusFilter = keyof typeof statusFilters;

/** 絞り込みと法人名の検索に合う行だけを残す */
export const filterRows = (
  rows: MatchedRow[],
  inputs: Map<number, InputRow>,
  filter: StatusFilter,
  keyword: string
): MatchedRow[] => {
  const word = keyword.trim();
  return rows.filter((row) => {
    if (
      filter === "REVIEW" &&
      !["NEEDS_REVIEW", "UNMATCHED"].includes(row.status)
    )
      return false;
    if (filter === "ERROR" && row.status !== "ERROR") return false;
    if (filter === "DUPLICATE" && row.status !== "DUPLICATE") return false;
    if (filter === "TARGET" && row.targetState !== "TARGET") return false;
    if (!word) return true;
    const names = [
      row.corporation?.companyName,
      inputs.get(row.rowNumber)?.companyName,
    ];
    return names.some((name) => name?.includes(word));
  });
};
